import pdfMake from 'pdfmake/build/pdfmake';
import { Content, TDocumentDefinitions } from 'pdfmake/interfaces';
import pdfFonts from 'pdfmake/build/vfs_fonts';
import { getCorporativoInfo, getDataBase64 } from '../../lib/Util';
import { IReportHoursQuantified } from '../../system/reporting/reporte-horas-cuantificadas';
import { DateTime } from 'luxon';
import { Company } from '../../system/company/Company';
import { t } from 'i18next';
(pdfMake as any).vfs = (pdfFonts as any).pdfMake?.vfs ?? (pdfFonts as any).vfs;

const buildHierarchy = (rows: IReportHoursQuantified[]) => {
  const result: Record<string, Record<string, Record<string, IReportHoursQuantified[]>>> = {};

  rows.forEach((row) => {
    const area = row.Area || 'Sin área';
    const concepto = row.Concepto || 'Sin concepto';
    const proyecto = row.Nombre_de_Servicio_o_Proyecto || 'Sin proyecto';

    result[area] ??= {};
    result[area][concepto] ??= {};
    result[area][concepto][proyecto] ??= [];

    result[area][concepto][proyecto].push(row);
  });

  return result;
};

export const exportQuantifiedHoursPdf = async ({
  period,
  getFilteredData,
  company,
  node,
}: {
  period: DateTime | undefined;
  node: string;
  company: Company | null;
  getFilteredData: () => Promise<IReportHoursQuantified[]>;
}) => {
  const corporativo = getCorporativoInfo();

  const IMAGE_LOGO_BASE64 = getDataBase64();

  const logoBase64 = IMAGE_LOGO_BASE64.startsWith('data:image') ? IMAGE_LOGO_BASE64 : `data:image/png;base64,${IMAGE_LOGO_BASE64}`;

  if (!corporativo || !period) return;

  const data = await getFilteredData();
  const hierarchy = buildHierarchy(data);

  const body: pdfMake.TableCell[][] = [
    [
      {
        text: t('Área').toString(),
        style: 'tableHeader',
      },
      {
        text: t('Concepto').toString(),
        style: 'tableHeader',
      },
      {
        text: t('Proyecto / Servicio').toString(),
        style: 'tableHeader',
      },
      {
        text: t('Detalle Actividad').toString(),
        style: 'tableHeader',
      },
      {
        text: t('Horas').toString(),
        style: 'tableHeader',
      },
    ],
  ];

  Object.entries(hierarchy).forEach(([area, conceptos]) => {
    const areaRows = Object.values(conceptos)
      .flatMap((proyectos) => Object.values(proyectos))
      .reduce((acc, rows) => acc + rows.length, 0);

    let areaAdded = false;

    Object.entries(conceptos).forEach(([concepto, proyectos]) => {
      const conceptoRows = Object.values(proyectos).reduce((acc, rows) => acc + rows.length, 0);

      let conceptoAdded = false;

      Object.entries(proyectos).forEach(([proyecto, rows]) => {
        let proyectoAdded = false;

        rows.forEach((row) => {
          const currentRow: pdfMake.TableCell[] = [];

          if (!areaAdded) {
            currentRow.push({
              text: area,
              rowSpan: areaRows,
              bold: true,
              alignment: 'center',
              margin: [0, 5],
            });

            areaAdded = true;
          } else {
            currentRow.push({});
          }

          if (!conceptoAdded) {
            currentRow.push({
              text: concepto,
              rowSpan: conceptoRows,
              alignment: 'center',
              margin: [0, 5],
            });

            conceptoAdded = true;
          } else {
            currentRow.push({});
          }

          if (!proyectoAdded) {
            currentRow.push({
              text: proyecto,
              rowSpan: rows.length,
              alignment: 'center',
              margin: [0, 5],
            });

            proyectoAdded = true;
          } else {
            currentRow.push({});
          }

          currentRow.push(
            {
              text: row.act_nombre,
            },
            {
              text: parseFloat(row.Horas_Distribuido).toFixed(4),
              alignment: 'right',
            },
          );

          body.push(currentRow);
        });
      });
    });
  });

  const headerStack: Content[] = [
    {
      text: `${company ? company.getName() : corporativo.empp_nombre} ${period.toFormat('yyyy')}`,
      alignment: 'center',
      color: '#0078D4',
      fontSize: 14,
    },
  ];

  if (node) {
    headerStack.push({
      text: data[0]?.Area ?? '',
      alignment: 'center',
      fontSize: 12,
      margin: [0, 5, 0, 0],
    });
  }

  headerStack.push({
    text: period.setLocale('es').toFormat('LLLL').toUpperCase(),
    alignment: 'center',
    color: '#0078D4',
    bold: true,
    fontSize: 18,
    margin: [0, 8, 0, 0],
  });

  const docDefinition: TDocumentDefinitions = {
    pageSize: 'A4',
    pageMargins: [20, 90, 20, 40],
    header: {
      margin: [20, 15, 20, 10],
      columns: [
        {
          image: logoBase64,
          width: 100,
        },
        {
          width: '*',
          stack: headerStack,
        },
      ],
    },

    footer: (currentPage: number, pageCount: number) => ({
      margin: [20, 10],
      columns: [
        {
          text: corporativo.empp_nombre,
          alignment: 'center',
          fontSize: 8,
          color: '#666',
        },
      ],
    }),

    content: [
      {
        table: {
          headerRows: 1,
          widths: ['auto', 'auto', 'auto', '*', 30],
          body,
        },

        layout: {
          fillColor: (rowIndex: number) => {
            if (rowIndex === 0) return '#0078D4';
            return null;
          },

          hLineWidth: () => 0.5,
          vLineWidth: () => 0.5,

          hLineColor: () => '#d1d5db',
          vLineColor: () => '#d1d5db',

          paddingLeft: () => 4,
          paddingRight: () => 4,
          paddingTop: () => 3,
          paddingBottom: () => 3,
        },
      },
    ],

    styles: {
      tableHeader: {
        color: 'white',
        bold: true,
        alignment: 'center',
        fontSize: 9,
      },
    },

    defaultStyle: {
      fontSize: 8,
    },
  };

  pdfMake.createPdf(docDefinition).download(`reporte-horas-cuantificadas-${period.toFormat('yyyy-MM')}.pdf`);
};
